import { isUndefined } from "@gamedev-web/gamedev-sdk-core";

type KeysOfUnion<T> = T extends Record<string, unknown> ? keyof T : never;
type RequiredKeys<T, K extends keyof T> = Exclude<T, K> & Required<Pick<T, K>>;
type ValueType<T> = T extends { [key: string]: infer TValue } ? TValue : unknown;

export const removeUndefinedValues = <T extends Record<string, unknown>>(data: T) =>
  Object.keys(data).reduce((result, key) => {
    const value = data[key];
    if (!isUndefined(value)) {
      result[key] = value;
    }

    return result;
  }, {});

export const withLowerCaseKeys = <T extends Record<string, unknown>>(obj: T) =>
  Object.keys(obj).reduce((aggregate, current) => {
    aggregate[current.toLowerCase()] = obj[current];
    return aggregate;
  }, {});

export const getDefaultValueFromSingleOrArray = <T>(value: T | T[] | undefined) =>
  value ? (Array.isArray(value) ? value[0] : value) : undefined;

export const getValueCaseInsensitive = <
  TObject extends { [key: string]: TValue },
  TValue = ValueType<TObject>,
>(
  data: TObject,
  lookupKey: string
): TValue | undefined => {
  const lookupKeyLowerCase = lookupKey.toLowerCase();
  for (const key in data) {
    if (key.toLowerCase() === lookupKeyLowerCase) {
      return data[key];
    }
  }

  return undefined;
};

export const hasKey = <T, K extends KeysOfUnion<T>>(data: T, key: K): data is RequiredKeys<T, K> =>
  Object.prototype.hasOwnProperty.call(data, key);

export const isEmpty = <T extends Record<string, unknown>>(data: T | undefined) =>
  !data || Object.keys(data).length === 0;

export const isNotEmpty = <T extends Record<string, unknown>>(data: T | undefined): data is T =>
  !isEmpty(data);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const objectWithoutKey = (dict: { [k: string]: any }, key: string) => {
  const { [key]: _deletedKey, ...otherKeys } = dict;
  return otherKeys;
};
